/**
 * MONSTER TOWN — CANVAS ICONS FOR THE CREATOR. MONSTER CREW (src/monster/**).
 *
 * LAW #4: no reading required. Every choice a kid makes is a PICTURE of the
 * thing that will happen. All of it is drawn in code — zero external assets.
 *
 * Each painter draws into a square 2D context of side `s`, origin top-left.
 */

import { css } from '../core/palette'
import type { PaletteName } from '../core/palette'
import type { MonsterBodyShape, MonsterEyes, MonsterHorns, MonsterPattern } from '../core/types'

type Ctx = CanvasRenderingContext2D
export type IconPainter = (c: Ctx, s: number) => void

const INK = css('ink')

function ellipse(c: Ctx, x: number, y: number, rx: number, ry: number, fill: string): void {
  c.beginPath()
  c.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2)
  c.fillStyle = fill
  c.fill()
  c.lineWidth = Math.max(1.5, rx * 0.16)
  c.strokeStyle = INK
  c.stroke()
}

function roundRect(c: Ctx, x: number, y: number, w: number, h: number, r: number, fill: string): void {
  c.beginPath()
  c.moveTo(x + r, y)
  c.arcTo(x + w, y, x + w, y + h, r)
  c.arcTo(x + w, y + h, x, y + h, r)
  c.arcTo(x, y + h, x, y, r)
  c.arcTo(x, y, x + w, y, r)
  c.closePath()
  c.fillStyle = fill
  c.fill()
  c.lineWidth = Math.max(1.5, w * 0.09)
  c.strokeStyle = INK
  c.stroke()
}

/** The body silhouette for a shape choice, in the kid's current colour. */
export function shapeIcon(shape: MonsterBodyShape, color: PaletteName): IconPainter {
  return (c, s) => {
    const fill = css(color)
    const cx = s / 2
    switch (shape) {
      case 'blob':
        ellipse(c, cx, s * 0.56, s * 0.3, s * 0.32, fill)
        roundRect(c, cx - s * 0.22, s * 0.82, s * 0.16, s * 0.1, s * 0.04, fill)
        roundRect(c, cx + s * 0.06, s * 0.82, s * 0.16, s * 0.1, s * 0.04, fill)
        break
      case 'tall':
        // Rounded, and NARROWER at the head than at the body — the icon has to
        // promise the shape the kid actually gets (see shapes.ts `tall`).
        ellipse(c, cx, s * 0.54, s * 0.18, s * 0.22, fill)
        ellipse(c, cx, s * 0.28, s * 0.16, s * 0.15, fill)
        roundRect(c, cx - s * 0.13, s * 0.7, s * 0.1, s * 0.22, s * 0.05, fill)
        roundRect(c, cx + s * 0.03, s * 0.7, s * 0.1, s * 0.22, s * 0.05, fill)
        break
      case 'round':
        ellipse(c, cx, s * 0.53, s * 0.36, s * 0.33, fill)
        roundRect(c, cx - s * 0.24, s * 0.8, s * 0.16, s * 0.09, s * 0.04, fill)
        roundRect(c, cx + s * 0.08, s * 0.8, s * 0.16, s * 0.09, s * 0.04, fill)
        break
      case 'chunky':
        // Wide barrel body, big round head, short legs.
        ellipse(c, cx, s * 0.6, s * 0.3, s * 0.19, fill)
        ellipse(c, cx, s * 0.33, s * 0.22, s * 0.21, fill)
        roundRect(c, cx - s * 0.22, s * 0.74, s * 0.16, s * 0.14, s * 0.06, fill)
        roundRect(c, cx + s * 0.06, s * 0.74, s * 0.16, s * 0.14, s * 0.06, fill)
        break
      default:
        ellipse(c, cx, s * 0.55, s * 0.3, s * 0.32, fill)
    }
  }
}

/** A fat paint blob of one body colour. */
export function colorIcon(color: PaletteName): IconPainter {
  return (c, s) => {
    ellipse(c, s / 2, s / 2, s * 0.34, s * 0.34, css(color))
    // A highlight so the swatch reads as a glossy blob, not a flat circle.
    c.beginPath()
    c.ellipse(s * 0.4, s * 0.38, s * 0.09, s * 0.06, -0.5, 0, Math.PI * 2)
    c.fillStyle = 'rgba(255,255,255,0.55)'
    c.fill()
  }
}

export function eyesIcon(kind: MonsterEyes): IconPainter {
  return (c, s) => {
    ellipse(c, s / 2, s * 0.54, s * 0.34, s * 0.3, css('monsterCream'))
    const eye = (x: number, r: number, squash: number): void => {
      c.save()
      c.translate(x, s * 0.5)
      c.scale(1, squash)
      ellipse(c, 0, 0, r, r, '#ffffff')
      c.restore()
      c.beginPath()
      c.ellipse(x, s * 0.5, r * 0.5, r * 0.5 * Math.max(0.35, squash), 0, 0, Math.PI * 2)
      c.fillStyle = INK
      c.fill()
    }
    if (kind === 'one') eye(s / 2, s * 0.16, 1)
    else if (kind === 'three') {
      eye(s * 0.32, s * 0.085, 1)
      eye(s * 0.5, s * 0.085, 1)
      eye(s * 0.68, s * 0.085, 1)
    } else if (kind === 'sleepy') {
      eye(s * 0.38, s * 0.12, 0.35)
      eye(s * 0.62, s * 0.12, 0.35)
    } else {
      eye(s * 0.37, s * 0.12, 1)
      eye(s * 0.63, s * 0.12, 1)
    }
  }
}

export function hornsIcon(kind: MonsterHorns): IconPainter {
  return (c, s) => {
    const horn = css('horn')
    const spike = (x: number, tipX: number, tipY: number): void => {
      c.beginPath()
      c.moveTo(x - s * 0.06, s * 0.62)
      c.lineTo(x + s * 0.06, s * 0.62)
      c.lineTo(tipX, tipY)
      c.closePath()
      c.fillStyle = horn
      c.fill()
      c.lineWidth = s * 0.05
      c.strokeStyle = INK
      c.stroke()
    }
    ellipse(c, s / 2, s * 0.74, s * 0.28, s * 0.18, css('monsterCream'))
    switch (kind) {
      case 'none':
        c.beginPath()
        c.arc(s / 2, s * 0.42, s * 0.13, 0, Math.PI * 2)
        c.lineWidth = s * 0.055
        c.strokeStyle = css('uiDisabled')
        c.stroke()
        c.beginPath()
        c.moveTo(s * 0.4, s * 0.32)
        c.lineTo(s * 0.6, s * 0.52)
        c.stroke()
        break
      case 'nubs':
        spike(s * 0.36, s * 0.36, s * 0.44)
        spike(s * 0.64, s * 0.64, s * 0.44)
        break
      case 'curved':
        spike(s * 0.34, s * 0.2, s * 0.3)
        spike(s * 0.66, s * 0.8, s * 0.3)
        break
      case 'antlers':
        spike(s * 0.36, s * 0.32, s * 0.2)
        spike(s * 0.64, s * 0.68, s * 0.2)
        c.lineWidth = s * 0.06
        c.strokeStyle = INK
        c.beginPath()
        c.moveTo(s * 0.34, s * 0.34)
        c.lineTo(s * 0.2, s * 0.26)
        c.moveTo(s * 0.66, s * 0.34)
        c.lineTo(s * 0.8, s * 0.26)
        c.stroke()
        break
      case 'antennae':
        c.lineWidth = s * 0.055
        c.strokeStyle = INK
        c.beginPath()
        c.moveTo(s * 0.42, s * 0.62)
        c.lineTo(s * 0.34, s * 0.32)
        c.moveTo(s * 0.58, s * 0.62)
        c.lineTo(s * 0.66, s * 0.32)
        c.stroke()
        ellipse(c, s * 0.34, s * 0.28, s * 0.07, s * 0.07, css('flowerYellow'))
        ellipse(c, s * 0.66, s * 0.28, s * 0.07, s * 0.07, css('flowerYellow'))
        break
      default:
        break
    }
  }
}

export function patternIcon(
  kind: MonsterPattern,
  color: PaletteName,
  accent: PaletteName,
): IconPainter {
  return (c, s) => {
    const fill = css(color)
    const acc = css(accent)
    ellipse(c, s / 2, s / 2, s * 0.33, s * 0.35, fill)
    c.save()
    c.beginPath()
    c.ellipse(s / 2, s / 2, s * 0.31, s * 0.33, 0, 0, Math.PI * 2)
    c.clip()
    switch (kind) {
      case 'plain':
        break
      case 'belly':
        c.beginPath()
        c.ellipse(s / 2, s * 0.58, s * 0.2, s * 0.22, 0, 0, Math.PI * 2)
        c.fillStyle = acc
        c.fill()
        break
      case 'spots':
        for (const [x, y, r] of [
          [0.36, 0.4, 0.08],
          [0.62, 0.5, 0.07],
          [0.46, 0.66, 0.06],
        ] as const) {
          c.beginPath()
          c.arc(s * x, s * y, s * r, 0, Math.PI * 2)
          c.fillStyle = acc
          c.fill()
        }
        break
      case 'stripes':
        c.fillStyle = acc
        for (let i = 0; i < 3; i++) c.fillRect(s * 0.1, s * (0.34 + i * 0.16), s * 0.8, s * 0.07)
        break
      case 'star': {
        c.fillStyle = acc
        c.beginPath()
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? s * 0.2 : s * 0.09
          const a = -Math.PI / 2 + (i * Math.PI) / 5
          const x = s / 2 + Math.cos(a) * r
          const y = s / 2 + Math.sin(a) * r
          if (i === 0) c.moveTo(x, y)
          else c.lineTo(x, y)
        }
        c.closePath()
        c.fill()
        break
      }
      default:
        break
    }
    c.restore()
  }
}

/** Hat glyphs. `null` draws the "no hat" head. */
export function hatIcon(id: string | null): IconPainter {
  return (c, s) => {
    ellipse(c, s / 2, s * 0.68, s * 0.26, s * 0.22, css('monsterCream'))
    if (id === null) {
      c.beginPath()
      c.arc(s / 2, s * 0.34, s * 0.12, 0, Math.PI * 2)
      c.lineWidth = s * 0.05
      c.strokeStyle = css('uiDisabled')
      c.stroke()
      c.beginPath()
      c.moveTo(s * 0.41, s * 0.25)
      c.lineTo(s * 0.59, s * 0.43)
      c.stroke()
      return
    }
    switch (id) {
      case 'party':
        c.beginPath()
        c.moveTo(s * 0.5, s * 0.12)
        c.lineTo(s * 0.68, s * 0.48)
        c.lineTo(s * 0.32, s * 0.48)
        c.closePath()
        c.fillStyle = css('flowerPink')
        c.fill()
        c.lineWidth = s * 0.055
        c.strokeStyle = INK
        c.stroke()
        ellipse(c, s * 0.5, s * 0.12, s * 0.06, s * 0.06, css('flowerYellow'))
        break
      case 'crown':
        c.beginPath()
        c.moveTo(s * 0.28, s * 0.48)
        c.lineTo(s * 0.28, s * 0.24)
        c.lineTo(s * 0.39, s * 0.36)
        c.lineTo(s * 0.5, s * 0.2)
        c.lineTo(s * 0.61, s * 0.36)
        c.lineTo(s * 0.72, s * 0.24)
        c.lineTo(s * 0.72, s * 0.48)
        c.closePath()
        c.fillStyle = css('uiCoin')
        c.fill()
        c.lineWidth = s * 0.055
        c.strokeStyle = INK
        c.stroke()
        break
      case 'bow':
        ellipse(c, s * 0.38, s * 0.34, s * 0.12, s * 0.09, css('flowerRed'))
        ellipse(c, s * 0.62, s * 0.34, s * 0.12, s * 0.09, css('flowerRed'))
        ellipse(c, s * 0.5, s * 0.34, s * 0.06, s * 0.06, css('flowerPink'))
        break
      case 'chef':
        roundRect(c, s * 0.32, s * 0.4, s * 0.36, s * 0.1, s * 0.03, css('flowerWhite'))
        ellipse(c, s * 0.5, s * 0.28, s * 0.2, s * 0.14, css('flowerWhite'))
        break
      case 'flower':
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2
          ellipse(
            c,
            s * 0.5 + Math.cos(a) * s * 0.11,
            s * 0.32 + Math.sin(a) * s * 0.11,
            s * 0.08,
            s * 0.08,
            css('flowerPurple'),
          )
        }
        ellipse(c, s * 0.5, s * 0.32, s * 0.07, s * 0.07, css('flowerYellow'))
        break
      case 'beanie':
        ellipse(c, s * 0.5, s * 0.34, s * 0.2, s * 0.15, css('wallLilac'))
        roundRect(c, s * 0.28, s * 0.42, s * 0.44, s * 0.09, s * 0.04, css('flowerWhite'))
        break
      case 'sunhat':
        roundRect(c, s * 0.2, s * 0.42, s * 0.6, s * 0.08, s * 0.04, css('sand'))
        ellipse(c, s * 0.5, s * 0.32, s * 0.16, s * 0.12, css('sand'))
        roundRect(c, s * 0.36, s * 0.38, s * 0.28, s * 0.06, s * 0.02, css('flowerRed'))
        break
      default:
        // cap, and any hat added later
        ellipse(c, s * 0.5, s * 0.34, s * 0.2, s * 0.14, css('roofTeal'))
        roundRect(c, s * 0.46, s * 0.4, s * 0.3, s * 0.07, s * 0.03, css('roofTeal'))
        break
    }
  }
}

// ---------------------------------------------------------------------------
// category + control glyphs
// ---------------------------------------------------------------------------

/**
 * SIX BUTTONS THAT LOOK NOTHING LIKE EACH OTHER (LAW #4).
 *
 * Round two turned these from abstract glyphs into "the same little monster with
 * one thing changed", which fixed the wrong half of the problem. Round three
 * photographed the result (`shots/kid-r3/01-first-launch.ipad.png`): six 88px
 * buttons holding six near-identical monster silhouettes, differing only in a
 * detail a few pixels across. Telling *eyes* from *horns* from *pattern* was a
 * visual discrimination a three-year-old will not make, so the row became random
 * poking on the first screen of the game.
 *
 * The rule now: EACH BUTTON IS A BIG PICTURE OF THE THING ITSELF, and no two
 * share a dominant shape or a dominant colour.
 *
 *   body    two different bodies, side by side   cream silhouettes
 *   colour  three fat blobs of paint             coral / yellow / blue
 *   pattern one spotty swatch                    pink disc, cream spots
 *   eyes    ONE eye, filling the button          white and ink
 *   horns   two horns, filling the button        cream spikes on teal
 *   hat     one party hat, filling the button    pink cone, yellow pom
 *
 * They are still literal pictures of what will happen — the round-two lesson —
 * they are just each drawn at the size of the whole button instead of as a
 * three-pixel difference on a shared drawing. A kid who taps the wrong one still
 * loses nothing: the row above updates instantly and nothing is committed.
 */
function miniFeet(c: Ctx, s: number, fill: string): void {
  roundRect(c, s * 0.28, s * 0.7, s * 0.17, s * 0.16, s * 0.06, fill)
  roundRect(c, s * 0.55, s * 0.7, s * 0.17, s * 0.16, s * 0.06, fill)
}

/** Two friendly eyes, sized to a head of radius `r` centred at (cx, cy). */
function miniEyes(c: Ctx, cx: number, cy: number, r: number): void {
  for (const sign of [-1, 1]) {
    const x = cx + sign * r * 0.42
    ellipse(c, x, cy, r * 0.32, r * 0.34, '#ffffff')
    c.beginPath()
    c.ellipse(x + sign * r * 0.05, cy + r * 0.05, r * 0.15, r * 0.15, 0, 0, Math.PI * 2)
    c.fillStyle = INK
    c.fill()
  }
}

/** The whole little creature: feet, body, face. */
function miniMonster(c: Ctx, s: number, fill: string, o: { eyes?: boolean } = {}): void {
  miniFeet(c, s, fill)
  ellipse(c, s * 0.5, s * 0.47, s * 0.31, s * 0.32, fill)
  if (o.eyes === true) miniEyes(c, s * 0.5, s * 0.44, s * 0.31)
}

/** Fill + ink a path already on the context. */
function inkPath(c: Ctx, s: number, fill: string, weight = 0.06): void {
  c.fillStyle = fill
  c.fill()
  c.lineWidth = s * weight
  c.lineJoin = 'round'
  c.strokeStyle = INK
  c.stroke()
}

/** BODY — a tall body and a round body, side by side: "which one". */
export const catShapeIcon: IconPainter = (c, s) => {
  const fill = css('uiPanelAlt')
  // the round one, behind on the right
  ellipse(c, s * 0.71, s * 0.6, s * 0.25, s * 0.25, fill)
  roundRect(c, s * 0.55, s * 0.79, s * 0.13, s * 0.1, s * 0.05, fill)
  roundRect(c, s * 0.75, s * 0.79, s * 0.13, s * 0.1, s * 0.05, fill)
  // the tall one, in front on the left
  roundRect(c, s * 0.11, s * 0.34, s * 0.28, s * 0.44, s * 0.13, fill)
  ellipse(c, s * 0.25, s * 0.26, s * 0.17, s * 0.16, fill)
  roundRect(c, s * 0.13, s * 0.74, s * 0.1, s * 0.16, s * 0.05, fill)
  roundRect(c, s * 0.28, s * 0.74, s * 0.1, s * 0.16, s * 0.05, fill)
}

/** COLOUR — three fat blobs of paint. No creature: this button is about paint. */
export const catColorIcon: IconPainter = (c, s) => {
  const blobs: readonly (readonly [number, number, number, PaletteName])[] = [
    [0.33, 0.34, 0.24, 'monsterCoral'],
    [0.7, 0.36, 0.22, 'monsterSunny'],
    [0.5, 0.7, 0.25, 'monsterSky'],
  ]
  for (const [x, y, r, col] of blobs) {
    ellipse(c, s * x, s * y, s * r, s * r, css(col))
    c.beginPath()
    c.ellipse(s * (x - r * 0.3), s * (y - r * 0.36), s * r * 0.26, s * r * 0.17, -0.5, 0, Math.PI * 2)
    c.fillStyle = 'rgba(255,255,255,0.6)'
    c.fill()
  }
}

/** PATTERN — one big spotty swatch. The spots are the whole picture. */
export const catPatternIcon: IconPainter = (c, s) => {
  ellipse(c, s * 0.5, s * 0.5, s * 0.38, s * 0.38, css('monsterBubblegum'))
  c.save()
  c.beginPath()
  c.ellipse(s * 0.5, s * 0.5, s * 0.35, s * 0.35, 0, 0, Math.PI * 2)
  c.clip()
  for (const [x, y, r] of [
    [0.34, 0.35, 0.14],
    [0.68, 0.48, 0.12],
    [0.44, 0.71, 0.11],
  ] as const) {
    ellipse(c, s * x, s * y, s * r, s * r, css('monsterCream'))
  }
  c.restore()
}

/** EYES — ONE eye, as big as the button. Nothing else looks like this. */
export const catEyesIcon: IconPainter = (c, s) => {
  ellipse(c, s * 0.5, s * 0.5, s * 0.4, s * 0.34, '#ffffff')
  ellipse(c, s * 0.54, s * 0.53, s * 0.17, s * 0.17, INK)
  c.beginPath()
  c.ellipse(s * 0.45, s * 0.44, s * 0.07, s * 0.055, -0.5, 0, Math.PI * 2)
  c.fillStyle = '#ffffff'
  c.fill()
}

/** HORNS — two horns, as big as the button, on a sliver of scalp. */
export const catHornsIcon: IconPainter = (c, s) => {
  const horn = css('horn')
  for (const sign of [-1, 1]) {
    c.beginPath()
    c.moveTo(s * (0.5 + sign * 0.06), s * 0.74)
    c.lineTo(s * (0.5 + sign * 0.34), s * 0.74)
    c.lineTo(s * (0.5 + sign * 0.28), s * 0.08)
    c.closePath()
    inkPath(c, s, horn)
  }
  // Just enough head for the horns to be standing on something.
  c.beginPath()
  c.ellipse(s * 0.5, s * 0.96, s * 0.36, s * 0.24, 0, Math.PI, 0)
  c.closePath()
  inkPath(c, s, css('monsterTeal'))
}

/** HAT — one hat, as big as the button, and nothing wearing it. */
export const catHatIcon: IconPainter = (c, s) => {
  c.beginPath()
  c.moveTo(s * 0.5, s * 0.12)
  c.lineTo(s * 0.84, s * 0.74)
  c.lineTo(s * 0.16, s * 0.74)
  c.closePath()
  inkPath(c, s, css('flowerPink'))
  roundRect(c, s * 0.1, s * 0.71, s * 0.8, s * 0.13, s * 0.06, css('flowerRed'))
  ellipse(c, s * 0.5, s * 0.11, s * 0.1, s * 0.1, css('flowerYellow'))
}

export const doneIcon: IconPainter = (c, s) => {
  ellipse(c, s / 2, s / 2, s * 0.36, s * 0.36, css('uiOk'))
  c.lineWidth = s * 0.12
  c.lineCap = 'round'
  c.lineJoin = 'round'
  c.strokeStyle = '#ffffff'
  c.beginPath()
  c.moveTo(s * 0.32, s * 0.52)
  c.lineTo(s * 0.45, s * 0.65)
  c.lineTo(s * 0.7, s * 0.36)
  c.stroke()
}

/**
 * The tick for the ONE button that ends this screen. The button itself is a big
 * green pill, so the glyph is just the mark — a tick inside a circle inside a
 * button reads as small print, and this control must not.
 */
export const bigDoneIcon: IconPainter = (c, s) => {
  c.lineWidth = s * 0.155
  c.lineCap = 'round'
  c.lineJoin = 'round'
  c.strokeStyle = '#ffffff'
  c.beginPath()
  c.moveTo(s * 0.2, s * 0.52)
  c.lineTo(s * 0.41, s * 0.74)
  c.lineTo(s * 0.8, s * 0.26)
  c.stroke()
}

/** Turn my monster round — an arrow curling about the little creature. */
export const turnIcon: IconPainter = (c, s) => {
  c.lineWidth = s * 0.085
  c.lineCap = 'round'
  c.strokeStyle = css('uiAccent')
  c.beginPath()
  c.arc(s * 0.5, s * 0.5, s * 0.37, Math.PI * 0.82, Math.PI * 0.24)
  c.stroke()
  c.beginPath()
  c.moveTo(s * 0.78, s * 0.14)
  c.lineTo(s * 0.9, s * 0.36)
  c.lineTo(s * 0.65, s * 0.35)
  c.closePath()
  c.fillStyle = css('uiAccent')
  c.fill()
  c.save()
  c.translate(s * 0.11, s * 0.13)
  miniMonster(c, s * 0.78, css('monsterTeal'), { eyes: true })
  c.restore()
}

export const diceIcon: IconPainter = (c, s) => {
  roundRect(c, s * 0.2, s * 0.2, s * 0.6, s * 0.6, s * 0.14, css('uiAccent'))
  c.fillStyle = '#ffffff'
  for (const [x, y] of [
    [0.35, 0.35],
    [0.5, 0.5],
    [0.65, 0.65],
    [0.65, 0.35],
    [0.35, 0.65],
  ] as const) {
    c.beginPath()
    c.arc(s * x, s * y, s * 0.055, 0, Math.PI * 2)
    c.fill()
  }
}

/** Paint a painter into a fresh retina-ish canvas element of `size` CSS px. */
export function iconCanvas(paint: IconPainter, size: number, dpr = 2): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = size * dpr
  canvas.height = size * dpr
  canvas.style.width = `${size}px`
  canvas.style.height = `${size}px`
  const c = canvas.getContext('2d')
  if (c !== null) {
    c.scale(dpr, dpr)
    paint(c, size)
  }
  return canvas
}
